import _ from "lodash";
import { v4 as uuid } from "uuid";
import { AssetType, HeaderPositionType, ListStyleType, OrnamentType, TextStyleType, TrayType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class ListCategory extends BaseCategory {
    get name() {
        return "List";
    }

    get type() {
        return SlideCategories.LIST;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The userinput contains a list of text items or bullet points. Could also work for a split layout with an image and a list.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt(options = {}) {
        return `Write a list of items for the slide based on the userinput.
                Write a short description for each item.
                If the userinput mentions a specific type of list (e.g., checklist | numbered list | icon list | text boxes | columns), write it in preferredListType - otherwise use "none".
                If the userinput describes a conclusion or summary to add at the bottom of the slide, include it in conclusion. Otherwise, leave conclusion empty.
                `;
    }

    getPromptSchema(options = {}) {
        return {
            items: this.getItemListSchema(options),
            preferredListType: "none | checklist | numbered list | icon list | text boxes | columns",
            ...this.getConclusionSchema(options)
        };
    }

    buildListModel(type, model) {
        switch (type) {
            case "icon list":
                return this.buildIconListSlide(model);
            case "text boxes":
            case "columns":
                return this.buildTextBoxesSlide(model);
            case "bullets":
            case "checklist":
            case "numbered list":
            default:
                return this.buildBulletPointSlide(model);
        }
    }

    getSlideModels(model) {
        let models = [
            model.items.length < 7 && this.buildBulletPointSlide(model, { trayType: TrayType.LEFT_INLINE, trayWidth: 470 }),
            this.buildBulletPointSlide(model, null),
            // model.items.length < 7 && this.buildBulletPointSlide(model, { trayType: TrayType.RIGHT_TRAY, trayWidth: 300 }),
            this.buildIconListSlide(model),
            this.buildTextBoxesSlide(model)
        ].filter(m => m);

        return models;

        // const listTypes = ["bullets", "icon list", "text boxes"];
        //
        // let firstType = model.preferredListType;
        // if (firstType === "none") {
        //     // pick one randomly
        //     firstType = listTypes[Math.floor(Math.random() * listTypes.length)];
        // }
        //
        // let models = [this.buildListModel(firstType, model)];
        //
        // // add other options as well
        // const otherTypes = listTypes.filter(t => t !== firstType);
        // for (let type of otherTypes) {
        //     models.push(this.buildListModel(type, model));
        // }
        //
        // return models;
    }

    buildBulletPointSlide(model, trayOptions) {
        let builder = new SlideModelBuilder("textlist", model, ["bullets", "numbered list", "checklist"].includes(model.preferredListType));
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        let listStyle = ListStyleType.BULLET;
        switch (model.preferredListType) {
            case "checklist":
                listStyle = ListStyleType.CHECKBOX;
                break;
            case "numbered list":
                listStyle = ListStyleType.NUMBERED;
                break;
        }

        let subListType = _.some(model.items, item => item.subbullets && item.subbullets.length > 1) ? ListStyleType.BULLET : ListStyleType.TEXT;

        let slideImagePrompt = model.slideImage?.prompt;

        if (!slideImagePrompt) {
            trayOptions = null; // no image prompt, so no tray
        }

        let cols = 1;
        if (model.items.length > 4 || !trayOptions) {
            cols = 2;
        }
        if (model.items.length > 6) {
            cols = 3;
            trayOptions = null; // remove image for very long lists
        }
        const itemsInCol = Math.ceil(model.items.length / cols);

        let col = {
            isMedia: false,
            text: {
                blocks: []
            }
        };
        const items = [col];
        for (let i = 0; i < model.items.length; i++) {
            if (i >= itemsInCol && items.length < cols) {
                col = {
                    isMedia: false,
                    text: {
                        blocks: []
                    }
                };
                items.push(col);
            }
            col.text.blocks.push(builder.createBlock(model.items[i].title, TextStyleType.BULLET_LIST, { listStyle, bulletColor: null, indent: 0 }));
            if (model.items[i].subbullets?.length > 0) {
                let subbullets = model.items[i].subbullets.map(subbullet =>
                    builder.createBlock(subbullet.text, TextStyleType.BULLET_LIST, { listStyle: subListType, indent: 1 })
                );
                col.text.blocks.push(...subbullets);
            } else {
                col.text.blocks.push(builder.createBlock(model.items[i].description, TextStyleType.BULLET_LIST, { listStyle: ListStyleType.TEXT, indent: 1 }));
            }
            col.text.blocks = col.text.blocks.filter(b => b);
        }

        builder.createPrimary({
            collectionColor: "theme",
            listStyle,
            items
        });
        if (slideImagePrompt && trayOptions) {
            builder.createSideTray({ imagePrompt: slideImagePrompt, ...trayOptions }, true);
        }
        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildIconListSlide(model) {
        let builder = new SlideModelBuilder("textgrid_vertical_icons", model, model.preferredListType === "icon list");
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        builder.createPrimary({
            collectionColor: "colorful",
            items: model.items.filter(o => o).map(item => ({
                id: uuid(),
                ...builder.createIcon(item.icon ?? "star"),
                text: {
                    blocks: [
                        builder.createBlock(item.title, TextStyleType.TITLE),
                        builder.createBlock(item.description, TextStyleType.BODY),
                        ...(item.subbullets ?? []).map(subbullet =>
                            builder.createBlock(subbullet.text, TextStyleType.BULLET_LIST, { listStyle: item.subbullets.length > 1 ? ListStyleType.BULLET : ListStyleType.TEXT, indent: 0 })
                        )
                    ].filter(b => b)
                }
            }))
        });
        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildTextBoxesSlide(model) {
        let builder = new SlideModelBuilder("textBoxGrid", model, model.preferredListType === "text boxes");
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        builder.createPrimary({
            collectionColor: "colorful",
            ornament: model.items.some(i => i.icon) ? OrnamentType.ICON : OrnamentType.NUMBER,
            fitToText: true,
            singleRow: model.items.length <= 4,
            items: model.items.filter(o => o).map(item => ({
                id: uuid(),
                color: null,
                text: {
                    blocks: [
                        builder.createBlock(item.title, TextStyleType.TITLE),
                        (!item.subbullets || item.subbullets.length == 0) && builder.createBlock(item.description, TextStyleType.BODY),
                        ...(item.subbullets ?? []).map(subbullet =>
                            builder.createBlock(subbullet.text, TextStyleType.BULLET_LIST, { listStyle: item.subbullets.length > 1 ? ListStyleType.BULLET : ListStyleType.TEXT, indent: 0 })
                        )
                    ].filter(b => b)
                },
                ornament: {
                    ...builder.createIcon(item.icon)
                }
            }))
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        return builder.model;
    }
}

